import React, { useState } from 'react';

// Tabs/Tab — mirrors react-bootstrap's activeKey/defaultActiveKey/onSelect + <Tab eventKey title>.
export interface TabProps {
  eventKey: string;
  title: React.ReactNode;
  disabled?: boolean;
  children?: React.ReactNode;
}
export const Tab: React.FC<TabProps> = ({ children }) => <>{children}</>;

export interface TabsProps {
  activeKey?: string;
  defaultActiveKey?: string;
  onSelect?: (key: string | null) => void;
  id?: string;
  className?: string;
  /** Extra classes on the active panel, e.g. so it can fill and scroll. */
  panelClassName?: string;
  children?: React.ReactNode;
}
export const Tabs: React.FC<TabsProps> = ({
  activeKey,
  defaultActiveKey,
  onSelect,
  className = '',
  panelClassName = '',
  children,
}) => {
  const tabs = React.Children.toArray(children).filter(React.isValidElement) as React.ReactElement<TabProps>[];
  const first = tabs[0]?.props.eventKey;
  const [internal, setInternal] = useState<string | undefined>(defaultActiveKey ?? first);
  const current = activeKey !== undefined ? activeKey : internal;
  const select = (k: string) => {
    if (activeKey === undefined) setInternal(k);
    onSelect?.(k);
  };
  const active = tabs.find((t) => t.props.eventKey === current);
  return (
    <div className={className}>
      {/* One row; extra tabs scroll sideways instead of wrapping to a second row (W-M7). */}
      <div role="tablist" className="tw-flex tw-flex-nowrap tw-gap-1 tw-overflow-x-auto tw-overflow-y-hidden tw-border-b tw-border-line">
        {tabs.map((t) => {
          const isActive = t.props.eventKey === current;
          return (
            <button
              key={t.props.eventKey}
              type="button"
              role="tab"
              disabled={t.props.disabled}
              onClick={() => select(t.props.eventKey)}
              className={`-tw-mb-px tw-shrink-0 tw-whitespace-nowrap tw-border-b-2 tw-px-3 tw-py-2 ${
                isActive
                  ? 'tw-border-primary tw-font-medium tw-text-primary'
                  : 'tw-border-transparent tw-text-content-secondary hover:tw-text-content'
              } disabled:tw-opacity-50`}
            >
              {t.props.title}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" className={`tw-pt-3 ${panelClassName}`}>
        {active?.props.children}
      </div>
    </div>
  );
};

export default Tabs;
